<template>
  <div class="ion-page">
    <ion-header>
      <ion-toolbar color="navbar">
        <ion-buttons slot="start">
          <ion-button @click="cancelForm">Cancel</ion-button>
        </ion-buttons>
        <ion-title>New Form</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="saveForm">Create</ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content padding>
      <ion-card color="light">
        <ion-item>
          <ion-icon color="tertiary" ios="ios-clipboard" md="md-clipboard" slot="start"></ion-icon>
          <ion-label color="primary">Template</ion-label>
          <ion-select interface="popover" :value="template" @ionChange="templateChanged">
            <ion-select-option :value="template" v-for="template in templates" v-bind:key="template">{{ template }}</ion-select-option>
          </ion-select>
        </ion-item>
      </ion-card>
      <ion-card color="light">
        <ion-item>
          <ion-icon color="tertiary" ios="ios-folder-open" md="md-folder-open" slot="start"></ion-icon>
          <ion-label color="primary">Name</ion-label>
          <ion-input type="text" spellcheck="true" required="true" inputmode="text" autocapitalize="true" :value="form.title" @ionInput="form.title = $event.target.value"></ion-input>
        </ion-item>
        <ion-item>
          <ion-icon color="tertiary" ios="ios-list" md="md-list" slot="start"></ion-icon>
          <ion-label color="primary">Description</ion-label>
          <ion-textarea type="text" spellcheck="true" required="false" inputmode="text" autocapitalize="true" :value="form.description" @ionInput="form.description = $event.target.value"></ion-textarea>
        </ion-item>
      </ion-card>
      <transition-group name="list">
        <ion-card color="light" v-for="field in form.fields" :key="field.id">
          <ion-item>
            <ion-icon color="tertiary" ios="ios-pricetag" md="md-pricetag" slot="start"></ion-icon>
            <ion-label color="primary">Field</ion-label>
            <ion-input type="text" spellcheck="true" required="true" inputmode="text" autocapitalize="true" :value="field.title" @ionInput="field.title = $event.target.value"></ion-input>
          </ion-item>
          <ion-item>
            <ion-icon color="tertiary" ios="ios-settings" md="md-settings" slot="start"></ion-icon>
            <ion-label color="primary">Type</ion-label>
            <ion-select interface="popover" :value="field.type" @ionChange="field.type = $event.target.value">
              <ion-select-option :value="key" v-for="(type, key) in types" :key="key">{{ type }}</ion-select-option>
            </ion-select>
          </ion-item>
          <ion-item v-if="field.type == 'select' || field.type == 'multiple'">
            <ion-icon color="tertiary" ios="ios-options" md="md-options" slot="start"></ion-icon>
            <ion-label color="primary">Options</ion-label>
            <ion-input type="text" spellcheck="true" inputmode="text" v-on:keyup.enter="() => addOption(field, $event)"></ion-input>
          </ion-item>
          <ion-item v-if="(field.type == 'select' || field.type == 'multiple') && field.options && field.options.flat().length > 0">
            <ion-label class="ion-text-wrap">
              <ion-chip tappable color="medium" outline="true" @click="removeOption(field, option)" v-for="(option, index) in field.options.flat()" :key="index">
                <ion-label>{{ option }}</ion-label>
                <ion-icon ios="ios-close-circle" md="md-close-circle"></ion-icon>
              </ion-chip>
            </ion-label>
          </ion-item>
          <ion-item>
            <ion-icon color="tertiary" ios="ios-checkmark" md="md-checkmark" slot="start"></ion-icon>
            <ion-label color="primary">Required</ion-label>
            <ion-checkbox slot="end" :checked="field.required" @ionChange="field.required = $event.target.value == 'on'"></ion-checkbox>
          </ion-item>
          <ion-item>
            <ion-button slot="end" fill="clear" @click="removeField(field)">Remove</ion-button>
          </ion-item>
        </ion-card>
      </transition-group>
      <ion-card color="light">
        <ion-button color="primary" expand="block" fill="clear" v-on:click="addField('text', '', false, [])">Add Field</ion-button>
      </ion-card>
    </ion-content>
  </div>
</template>

<script>
import Helpers from "../mixins/Helpers";
import Plugins from "../mixins/Plugins";
import UUID from "../models/Uuid";
import User from "../models/User";
import Form from "../models/Form";
import Field from "../models/Field";
import Fields from "../models/Fields";
import Entries from "../models/Entries";
import router from "../router";

const TEMPLATE_BLANK = "Blank Form";
const TEMPLATE_CONTACTS = "Contacts";
const TEMPLATE_BIRTHDAYS = "Birthdays";
const TEMPLATE_ANNIVERSARIES = "Anniversaries";
const TEMPLATE_EXPENSES = "Expense Tracker";
const TEMPLATE_CALORIES = "Calorie Counter";
const TEMPLATE_WORKOUTS = "Workouts";
const TEMPLATE_LOCATIONS = "Favorite Locations";

export default {
  router: router,
  name: "form-edit",
  mixins: [Helpers, Plugins],
  data() {
    return {
      form: new Form(),
      template: TEMPLATE_BLANK,
      templates: [TEMPLATE_BLANK, TEMPLATE_CONTACTS, TEMPLATE_WORKOUTS, TEMPLATE_BIRTHDAYS, TEMPLATE_ANNIVERSARIES, TEMPLATE_CALORIES, TEMPLATE_EXPENSES, TEMPLATE_LOCATIONS],
      types: {
        text: "Text",
        number: "Number",
        boolean: "Boolean",
        date: "Date",
        time: "Time",
        datetime: "DateTime",
        checkbox: "Checkbox",
        select: "Select",
        multiple: "Multiple",
        email: "Email",
        phone: "Phone",
        url: "URL",
        image: "Image",
        location: "Location",
        signature: "Signature",
        barcode: "Barcode",
        qrcode: "QRCode"
      }
    };
  },
  computed: {
    user() {
      return this.$store.getters.getUser();
    },
    offline() {
      let url = new URL(window.location.href);
      return url.searchParams.get("offline");
    }
  },
  async created() {
    let timestamp = new Date().toISOString();
    this.form = new Form({
      id: `${this.user.id}-${timestamp}`,
      created: timestamp,
      updated: timestamp,
      user: this.user.id
    });
    this.form.fields = new Fields();
    this.form.entries = new Entries();
    this.addField("text", "", false, []);
  },
  mounted() {
    console.log("Offline", this.offline);
  },
  methods: {
    templateChanged(event) {
      this.template = event.target.value;
      if (this.template == TEMPLATE_BLANK) {
        this.form.title = "";
        this.form.fields = new Fields();
        this.addField("text", "", false, []);
      } else if (this.template == TEMPLATE_CONTACTS) {
        this.form.title = TEMPLATE_CONTACTS;
        this.form.fields = new Fields();
        this.addField("text", "Name", true, []);
        this.addField("phone", "Phone", true, []);
        this.addField("email", "Email", true, []);
      } else if (this.template == TEMPLATE_BIRTHDAYS) {
        this.form.title = TEMPLATE_BIRTHDAYS;
        this.form.fields = new Fields();
        this.addField("text", "Name", true, []);
        this.addField("date", "Birthday", true, []);
      } else if (this.template == TEMPLATE_ANNIVERSARIES) {
        this.form.title = TEMPLATE_ANNIVERSARIES;
        this.form.fields = new Fields();
        this.addField("text", "Name", true, []);
        this.addField("date", "Anniversary", true, []);
      } else if (this.template == TEMPLATE_EXPENSES) {
        this.form.title = TEMPLATE_EXPENSES;
        this.form.fields = new Fields();
        this.addField("text", "Item", true, []);
        this.addField("number", "Amount", true, []);
        this.addField("text", "Description", false, []);
      } else if (this.template == TEMPLATE_CALORIES) {
        this.form.title = TEMPLATE_CALORIES;
        this.form.fields = new Fields();
        this.addField("text", "Food", true, []);
        this.addField("select", "Type", false, ["Fruit", "Vegetable", "Meat", "Poultry", "Fish", "Tofu", "Cereal", "Grain", "Bread", "Pasta", "Milk", "Cheese", "Yoghurt", "Seeds", "Nuts", "Beans"]);
        this.addField("number", "Calories", false, []);
      } else if (this.template == TEMPLATE_WORKOUTS) {
        this.form.title = TEMPLATE_WORKOUTS;
        this.form.fields = new Fields();
        this.addField("multiple", "Routine", true, ["Cardio", "Chest", "Arms", "Back", "Abs", "Legs", "Yoga", "Running", "Stretching"]);
        this.addField("select", "Intensity", false, ["Easy", "Medium", "Hard"]);
        this.addField("select", "Minutes", false, ["15", "30", "45", "60", "90", "120"]);
        this.addField("datetime", "Date", true, []);
      } else if (this.template == TEMPLATE_LOCATIONS) {
        this.form.title = TEMPLATE_LOCATIONS;
        this.form.fields = new Fields();
        this.addField("text", "Name", true, []);
        this.addField("location", "Location", true, []);
      }
    },
    cancelForm() {
      this.hideModal();
    },
    async saveForm() {
      if (this.form.title.length == 0) {
        this.showToast("Form name is required");
      } else if (this.form.fields.length == 0) {
        this.showToast("At least one field is required");
      } else {
        for (let field of this.form.fields) {
          if (field.title.length == 0) {
            this.showToast("The field title is required");
            return;
          }
          if (field.type.length == 0) {
            this.showToast("The field type is required");
            return;
          }
        }
        if (this.offline == false && (await this.hasConnection())) {
          let loading = await this.showLoading("Creating...");
          try {
            this.form.pending = false;
            let form = await this.form.save();
            await form.store();
            this.hideModal({
              form: form
            });
          } catch (error) {
            if (this.expiredToken(error)) {
              try {
                await this.$store.dispatch("userRefresh");
                let form = await this.form.save();
                await form.store();
                this.hideModal({
                  form: form
                });
              } catch (error2) {
                this.showError("Problem Creating Form", error2);
              }
            } else {
              this.showError("Problem Creating Form", error);
            }
          } finally {
            loading.dismiss();
          }
        } else {
          let loading = await this.showLoading("Saving...");
          try {
            this.form.pending = true;
            let form = await this.form.store();
            this.hideModal({
              form: form
            });
          } catch (error) {
            this.showError("Problem Saving Form", error);
          } finally {
            loading.dismiss();
          }
        }
      }
    },
    addField(type = "text", title = "", required = false, options = []) {
      let field = new Field({
        id: UUID.random(),
        type: type,
        title: title,
        options: options,
        required: required,
        icon: Field.getIcon(type),
        ordinal: this.form.fields.length + 1
      });
      this.form.fields.push(field);
    },
    addOption(field, event) {
      field.options.push(event.target.value);
      event.srcElement.value = "";
    },
    removeOption(field, option) {
      field.options = field.options.filter((_option) => _option != option);
    },
    removeField(field) {
      this.form.fields = this.form.fields.filter((_field) => _field.id != field.id);
    }
  }
};
</script>

<style scoped lang="scss">
.list-enter-active,
.list-leave-active {
  transition: all 1s ease;
}
.list-enter,
.list-leave-to {
  opacity: 0;
}
</style>